import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Snowflake, ArrowDownToLine, ArrowUpFromLine, Clock, Thermometer, Plus } from 'lucide-react';

export default function HubTimeslicePage() {
  const [timeslices, setTimeslices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState({ hub_device_id: '', location_name: '', product_description: '' });

  useEffect(() => {
    fetchData();
     
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tsRes, devRes] = await Promise.all([
        opsApi.listHubTimeslices({}),
        opsApi.getReadyDevices()
      ]);
      setTimeslices(tsRes.data || []);
      setDevices(devRes.data || []);
    } catch (err) {
      toast.error('Failed to load HUB data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!form.hub_device_id || !form.location_name) {
      toast.error('Select a HUB device and enter location');
      return;
    }
    try {
      setChecking(true);
      await opsApi.hubCheckIn(form);
      toast.success(`Product checked into ${form.location_name}`);
      setCheckInOpen(false);
      setForm({ hub_device_id: '', location_name: '', product_description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setChecking(false);
    }
  };

  const handleCheckOut = async (timesliceId) => {
    try {
      const res = await opsApi.hubCheckOut({ timeslice_id: timesliceId });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const active = timeslices.filter(t => t.status === 'checked_in');
  const history = timeslices.filter(t => t.status !== 'checked_in');

  return (
    <div className="p-6 space-y-6" data-testid="hub-timeslice-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">HUB Cold Room</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Check-in / Check-out monitoring for cold storage</p>
        </div>
        <Button onClick={() => setCheckInOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="check-in-btn">
          <ArrowDownToLine className="w-4 h-4 mr-2" />Check In
        </Button>
      </div>

      {/* Active Timeslices */}
      {active.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <Snowflake className="w-5 h-5 text-blue-500" />Currently in Cold Room ({active.length})
          </h2>
          <div className="grid gap-3">
            {active.map(t => (
              <Card key={t.id} className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Snowflake className="w-5 h-5 text-blue-600 animate-pulse" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{t.location_name}</p>
                      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-3">
                        {t.product_description && <span>{t.product_description}</span>}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />In since: {formatDate(t.check_in_at)}</span>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleCheckOut(t.id)} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30" data-testid={`check-out-${t.id}`}>
                    <ArrowUpFromLine className="w-4 h-4 mr-2" />Check Out
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal-500" />Timeslice History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 && active.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Snowflake className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No cold room records yet. Check in a product to start tracking.</p>
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No completed timeslices yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500">
                    <th className="text-left py-3 px-2">Location</th>
                    <th className="text-left py-3">Product</th>
                    <th className="text-left py-3">Check In</th>
                    <th className="text-left py-3">Check Out</th>
                    <th className="text-center py-3">Duration</th>
                    <th className="text-center py-3">Temp</th>
                    <th className="text-center py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(t => (
                    <tr key={t.id} className="border-b border-slate-100 dark:border-slate-700/50">
                      <td className="py-3 px-2 text-slate-900 dark:text-white">{t.location_name}</td>
                      <td className="text-slate-600 dark:text-slate-400">{t.product_description || '—'}</td>
                      <td className="text-slate-600 dark:text-slate-400">{formatDate(t.check_in_at)}</td>
                      <td className="text-slate-600 dark:text-slate-400">{formatDate(t.check_out_at)}</td>
                      <td className="text-center font-medium text-slate-900 dark:text-white">{t.duration_hours ? `${t.duration_hours}h` : '—'}</td>
                      <td className="text-center">
                        {t.temp_avg ? (
                          <span className="flex items-center justify-center gap-1 text-xs">
                            <Thermometer className="w-3 h-3 text-teal-500" />
                            {t.temp_avg.toFixed(1)}°C
                          </span>
                        ) : '—'}
                      </td>
                      <td className="text-center">
                        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {t.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check-In Dialog */}
      <Dialog open={checkInOpen} onOpenChange={setCheckInOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-teal-500" />Check In to Cold Room
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">HUB Device</label>
              <select
                value={form.hub_device_id}
                onChange={(e) => setForm(f => ({ ...f, hub_device_id: e.target.value }))}
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white p-2.5"
                data-testid="hub-device-select"
              >
                <option value="">Select HUB device...</option>
                {devices.map(d => (
                  <option key={d.id} value={d.id}>{d.device_uid} ({d.status})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Cold Room / Location</label>
              <Input value={form.location_name} onChange={(e) => setForm(f => ({ ...f, location_name: e.target.value }))} placeholder="e.g. Cold Room A, Freezer Unit 2" className="dark:bg-slate-900 dark:border-slate-700 dark:text-white" data-testid="location-input" />
            </div>
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Product (optional)</label>
              <Input value={form.product_description} onChange={(e) => setForm(f => ({ ...f, product_description: e.target.value }))} placeholder="e.g. Insulin batch, Biologicals" className="dark:bg-slate-900 dark:border-slate-700 dark:text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInOpen(false)} className="dark:border-slate-600 dark:text-slate-300">Cancel</Button>
            <Button onClick={handleCheckIn} disabled={checking} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-check-in-btn">
              {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
              Check In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
